import { useGateValue } from "@statsig/react-bindings";

import { useFeatureFlag } from "@/features/featureFlag";

/**
 * Whether this organization may create a single select custom field.
 *
 * This gate is the release mechanism for the whole feature, not a guard
 * against a bad menu entry: an organization that cannot create the field type
 * never produces one, so CAAC and MAAC have nothing to show either. That is
 * why CAAC carries no flag of its own.
 *
 * **It gates creation only.** Displaying an existing single select field and
 * editing its options are never blocked. The field can already exist when the
 * gate is off -- rollout reverted, or created straight through CDH -- and
 * hiding the UI then leaves the user with a field they can neither open nor
 * fix, which is worse than showing it.
 *
 * The local feature flag is a testing override, following
 * `useEnableVibeLab`. The Statsig gate is per-organization and opens late in
 * the release plan, so without it the creation flow cannot be exercised at
 * all before then.
 *
 * TODO(#819): drop the local half once the gate is open, or once the Statsig
 * gate override replaces the hand-written `||` here. The `||` only forces the
 * gate on, never off, so an organization whose gate is already open has no
 * local way to check the gate-off view.
 */
export const useEnableCustomFieldSingleSelect = (): boolean => {
  const localOverride = useFeatureFlag("enable-custom-field-single-select");
  const featureGate = useGateValue("enable_custom_field_single_select");
  return localOverride || featureGate;
};
